import React, { Fragment, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { X } from 'lucide-react';
import toast from 'react-hot-toast';
import { buchungenAPI } from '../lib/api';

const EMPTY = {
  soll_konto: '',
  haben_konto: '',
  betrag: '',
  ust_kennz: '',
  buchungstext: '',
  beleg_datum: new Date().toISOString().slice(0, 10),
};

export default function ManualBuchungModal({ open, onClose, slug, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        soll_konto: form.soll_konto.trim(),
        haben_konto: form.haben_konto.trim(),
        betrag: form.betrag,
        beleg_datum: form.beleg_datum,
      };
      if (form.ust_kennz) payload.ust_kennz = form.ust_kennz;
      if (form.buchungstext) payload.buchungstext = form.buchungstext;
      const r = await buchungenAPI.createManual(slug, payload);
      toast.success(`Buchung #${r.data.id} angelegt`);
      setForm(EMPTY);
      if (onSaved) await onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Buchung fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Transition show={open} as={Fragment}>
      <Dialog onClose={onClose} className="modal-overlay">
        <Transition.Child as={Fragment}
          enter="fade-enter" enterFrom="fade-enter-from" enterTo="fade-enter-to"
          leave="fade-leave" leaveFrom="fade-leave-from" leaveTo="fade-leave-to">
          <div className="modal-backdrop" aria-hidden="true" />
        </Transition.Child>

        <div className="modal-positioner">
          <Transition.Child as={Fragment}
            enter="slide-enter" enterFrom="slide-enter-from" enterTo="slide-enter-to"
            leave="slide-leave" leaveFrom="slide-leave-from" leaveTo="slide-leave-to">
            <Dialog.Panel className="modal-panel">
              <form onSubmit={onSubmit}>
                <div className="modal-header">
                  <Dialog.Title className="modal-title">Manuelle Buchung</Dialog.Title>
                  <button type="button" className="btn-text" onClick={onClose} aria-label="Schließen">
                    <X size={16} />
                  </button>
                </div>

                <Dialog.Description className="muted small">
                  Beleglose Direktbuchung — Soll an Haben. Geht direkt auf Status <b>gebucht</b>.
                </Dialog.Description>

                <div className="form-grid" style={{ gridTemplateColumns: '1fr 1fr', marginTop: 12 }}>
                  <label>
                    Soll-Konto *
                    <input
                      type="text" value={form.soll_konto} onChange={(e) => setField('soll_konto', e.target.value)}
                      placeholder="z.B. 4980" pattern="\d{3,8}" required autoFocus
                    />
                  </label>
                  <label>
                    Haben-Konto *
                    <input
                      type="text" value={form.haben_konto} onChange={(e) => setField('haben_konto', e.target.value)}
                      placeholder="z.B. 1000" pattern="\d{3,8}" required
                    />
                  </label>
                  <label>
                    Betrag (EUR) *
                    <input
                      type="number" step="0.01" min="0.01" value={form.betrag}
                      onChange={(e) => setField('betrag', e.target.value)} required
                    />
                  </label>
                  <label>
                    USt-Kennz
                    <select value={form.ust_kennz} onChange={(e) => setField('ust_kennz', e.target.value)}>
                      <option value="">— keiner —</option>
                      <option value="0">0 — ohne USt</option>
                      <option value="8">8 — Vorsteuer 7%</option>
                      <option value="9">9 — Vorsteuer 19%</option>
                    </select>
                  </label>
                  <label>
                    Beleg-Datum *
                    <input
                      type="date" value={form.beleg_datum}
                      onChange={(e) => setField('beleg_datum', e.target.value)} required
                    />
                  </label>
                  <label>
                    Buchungstext
                    <input
                      type="text" value={form.buchungstext} maxLength={60}
                      onChange={(e) => setField('buchungstext', e.target.value)}
                      placeholder="max 60 Zeichen"
                    />
                  </label>
                </div>

                <div className="actions" style={{ borderTop: 'none', paddingTop: 12 }}>
                  <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>
                    Abbrechen
                  </button>
                  <button type="submit" className="btn-primary" disabled={busy}>
                    Buchen
                  </button>
                </div>
              </form>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}
